import { useEffect, useState } from "react";
import { UtensilsCrossed, HardHat, Sparkles, Scale, ExternalLink } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Stagger, StaggerItem } from "./Reveal";
import { ProjectSlideshow } from "./ProjectSlideshow";
import { getProjects } from "@/lib/api";

const examples = [
  { id: "restaurant", icon: UtensilsCrossed, name: "Restaurant Website", type: "Example concept", accent: "from-amber-500/30 via-orange-500/10", bar: "from-amber-300 to-orange-400", tags: ["Menu", "Bookings", "Gallery"] },
  { id: "trades", icon: HardHat, name: "Trades Business Website", type: "Example concept", accent: "from-sky-500/30 via-blue-500/10", bar: "from-sky-300 to-blue-400", tags: ["Services", "Quote form", "Service areas"] },
  { id: "beauty", icon: Sparkles, name: "Beauty & Wellness Website", type: "Example concept", accent: "from-emerald-500/30 via-teal-500/10", bar: "from-emerald-300 to-teal-400", tags: ["Services", "Bookings", "Quote form"] },
  { id: "professional", icon: Scale, name: "Professional Services Website", type: "Example concept", accent: "from-violet-500/30 via-indigo-500/10", bar: "from-violet-300 to-indigo-400", tags: ["Team", "Services", "Contact"] },
];

const MiniSite = ({ accent, bar, icon: Icon }) => (
  <div className={`relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br ${accent} to-ink2`} aria-hidden="true">
    <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-black/20">
      <div className="h-2 w-12 rounded-full bg-white/30" />
      <div className="flex gap-2"><div className="h-2 w-6 rounded-full bg-white/15" /><div className="h-2 w-6 rounded-full bg-white/15" /><div className={`h-4 w-10 rounded-full bg-gradient-to-r ${bar}`} /></div>
    </div>
    <div className="absolute left-4 right-4 top-14 sm:top-16 flex gap-4">
      <div className="flex-1">
        <div className={`h-3 w-3/4 rounded-full bg-gradient-to-r ${bar} opacity-90`} />
        <div className="mt-2 h-3 w-1/2 rounded-full bg-white/50" />
        <div className="mt-3 h-1.5 w-full rounded-full bg-white/15" />
        <div className="mt-1.5 h-1.5 w-5/6 rounded-full bg-white/15" />
        <div className={`mt-4 h-6 w-20 rounded-full bg-gradient-to-r ${bar}`} />
      </div>
      <div className="grid h-24 w-24 sm:h-28 sm:w-28 shrink-0 place-items-center rounded-2xl border border-white/10 bg-white/[0.06]">
        <Icon className="h-9 w-9 text-white/70" />
      </div>
    </div>
    <div className="absolute inset-x-4 bottom-4 grid grid-cols-3 gap-2 group-hover:translate-y-0 translate-y-2 transition-transform duration-700">
      {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-xl border border-white/[0.08] bg-white/[0.05]" />)}
    </div>
  </div>
);

const ProjectCard = ({ p }) => (
  <article className="card-dark card-hover group p-4 sm:p-5" data-testid={`portfolio-project-${p.id}`}>
    <ProjectSlideshow project={p} />
    <div className="flex items-start justify-between gap-4 px-2 pt-5 pb-2">
      <div className="min-w-0">
        <h3 className="text-lg sm:text-xl font-semibold text-white">{p.title}</h3>
        {p.description && <p className="mt-1 text-sm text-slate-400 leading-relaxed">{p.description}</p>}
        {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-indigo-300 hover:text-white" data-testid={`portfolio-project-link-${p.id}`}>Visit website <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></a>}
      </div>
      {p.category && <span className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">{p.category}</span>}
    </div>
  </article>
);

export const Portfolio = () => {
  const [projects, setProjects] = useState(null);
  useEffect(() => { getProjects().then(setProjects).catch(() => setProjects([])); }, []);
  const real = projects && projects.length > 0;

  return (
    <section id="portfolio" className="section" aria-labelledby="portfolio-title">
      <div className="container-x">
        {real ? (
          <SectionHeading id="portfolio-title" eyebrow="Portfolio" title="Websites we've designed and built." subtitle="A selection of real websites created by ForbesOS Websites for businesses." />
        ) : (
          <SectionHeading id="portfolio-title" eyebrow="Example Website Designs" title="Design concepts for different types of businesses." subtitle="These are illustrative example concepts — not real client projects — showing the kinds of websites we design and build." />
        )}
        <Stagger className="mt-14 grid gap-6 md:grid-cols-2">
          {real
            ? projects.map((p) => <StaggerItem key={p.id}><ProjectCard p={p} /></StaggerItem>)
            : examples.map((p) => (
                <StaggerItem key={p.id}>
                  <article className="card-dark card-hover group p-4 sm:p-5" data-testid={`portfolio-card-${p.id}`}>
                    <MiniSite {...p} />
                    <div className="flex items-start justify-between gap-4 px-2 pt-5 pb-2">
                      <div>
                        <h3 className="text-lg sm:text-xl font-semibold text-white">{p.name}</h3>
                        <p className="mt-1 text-sm text-slate-400">{p.tags.join(" · ")}</p>
                      </div>
                      <span className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">{p.type}</span>
                    </div>
                  </article>
                </StaggerItem>
              ))}
        </Stagger>
      </div>
    </section>
  );
};
